import React, { forwardRef, createElement } from "react"
import classNames from "classnames"

import useHasMounted from "../hooks/useHasMounted"
import { useBreakpoints } from "../hooks/useBreakpoints"

import { MutuallyExclude } from "../utils/mutuallyExclusive"
import { TAllowedDirections, alignmentOptions, justifyOptions, paddingOptions, spacingOptions } from "./Stack.config"

import "./Stack.css"
const baseClass = "wb-stack"

/** A Stack is a CSS flex container that stacks children vertically or
 * horizontally, with equal spacing between each child, and optional padding.
  * Any non-specified props will be passed directly to the underlying container
  * node.
  *
  * Since components in the UI library have no padding or margin by default, the
  * container in which they are rendered is responsible for spacing. The
  * `<Stack>` component can be used to create gaps between components, in
  * addition to it's primary function as a flex layout container. */

/* This allows us the convenience of writing <Stack horizontal> instead of <Stack dir="horizontal">,
 * but we must do prop validation manually to ensure only one direction prop is passed as true. */

type ExclusiveDirectionProps = MutuallyExclude<TAllowedDirections, keyof TAllowedDirections>

type BaseProps = {
  /** Specify vertical stack (this corresponds to flex-direction: column). */
  // vertical: oneOfAllowedDirections,
  /** Specify horizontal stack (this corresponds to flex-direction: row). */
  // horizontal: oneOfAllowedDirections,
  /** Specify horizontal reverse stack (this corresponds to flex-direction: row-reverse). */
  // horizontalReverse: oneOfAllowedDirections,
  /** Specify whether the display property should be inline-flex */
  inline?: boolean,
  /** Specify the flex container should wrap when the container exceeds the
   * childrens width */
  wrap?: boolean,
  /** Defines the padding inside the container. */
  padding?: typeof paddingOptions[number],
  /** Defines the spacing between children. The raw values these correspond to depend on whether the stack is vertical or horizontal. */
  spacing?: typeof spacingOptions[number],
  /** These correspond to the align-items CSS property and are set directly in a style tag on the container. */
  alignItems?: typeof alignmentOptions[number],
  /** These correspond to the justify-content CSS property and are set directly in a style tag on the container. */
  justifyContent?: typeof justifyOptions[number],
  /** Allows overriding the root HTML tag. */
  as?: string,
  /** EXPERIMENTAL: swap the flex direction based on window size, specified and
   * only supported with px unit, for example 550px */
  swapDirectionAt?: string,
  /** Pass in a classname to further customise the styling. */
  className?: string,
  /** You can interact with inline styling as well. */
  style?: React.CSSProperties,
  /** Orientation overide */
  orientation?: keyof TAllowedDirections,
}

type StackProps = BaseProps & ExclusiveDirectionProps & {
  children?: React.ReactNode
}

const Stack = forwardRef<HTMLElement, StackProps>(({
  vertical,
  horizontal = false,
  orientation,
  horizontalReverse,
  inline,
  padding,
  spacing,
  alignItems,
  justifyContent,
  as = "div",
  swapDirectionAt,
  children,
  wrap = false,
  ...rest
}, ref) => {

  if (process.env.NODE_ENV === "development") {
    if (vertical && spacing === "xxxs") {
      console.warn("Stack: Invalid prop combination vertical with spacing=xxxs.\n" +
        "<Stack> spacing prop does not map one-to-one with space " +
        "option tokens – xxxs translates to inappropriately small " +
        "vertical space, rather use CSS directly in this case.")
    }

    if (vertical && horizontal) {
      console.warn("Stack: Invalid prop combination vertical and horizontal.\n" +
        "<Stack> cannot be in both directions at the same time")
    }
  }

  const { style: styleFromProps, className, ...restWithoutStyle } = rest
  // const hasMounted = useHasMounted()
  const windowSize = useBreakpoints()
  const direction = determineDirection(windowSize, vertical, horizontal, swapDirectionAt)

  const classes = classNames(
    baseClass,
    className, {
    [`${baseClass}--vertical`]: direction === 'vertical',
    [`${baseClass}--horizontal`]: direction === 'horizontal' || horizontalReverse,
    [`${baseClass}--reverse`]: horizontalReverse,
    [`${baseClass}--inset-${padding}`]: padding !== undefined,
    [`${baseClass}--spacing-${spacing}`]: spacing !== undefined,
    [`${baseClass}--wrap`]: wrap,
    [`${baseClass}--inline`]: inline
  }
  )

  const props = {
    className: classes,
    style: { alignItems, justifyContent, ...styleFromProps },
    ref,
    ...restWithoutStyle
  }

  // if (!hasMounted) {
  //   return null
  // }

  return createElement(
    as,
    props,
    children
  )
})

Stack.displayName = "Stack"
export { Stack }
export type { StackProps }

const determineDirection = (windowSize, vertical, horizontal, swapDirectionAt) => {
  if (vertical) {
    if (!swapDirectionAt) return 'vertical'

    const breakpointHeight = +swapDirectionAt.replace("px", "")
    return windowSize.height <= breakpointHeight ? 'horizontal' : 'vertical'
  }

  if (horizontal) {
    if (!swapDirectionAt) return 'horizontal'

    const breakpointWidth = +swapDirectionAt.replace("px", "")
    return windowSize.width <= breakpointWidth ? 'vertical' : 'horizontal'
  }
}
